import * as React from 'react';
import {
    SimpleForm,
    Toolbar as T,
    useUpdate,
    useNotify,
    useRefresh,
    useRecordContext,
    useCreatePath,
    useRedirect
} from 'react-admin';
import { Grid, Box, Stack } from '@mui/material';

import { sectionTitleStyle } from 'src/styles';
import { ChildFormFields, Resource } from 'src/constants';
import { ShsSaveButton, ShsButton, ShsDatePicker, ShsAutoCompleteWithLoad } from 'src/components/common';

const styles = {
    '&.MuiCardContent-root': {
        p: 0,
        m: 0
    },
    '&.MuiToolbar-root': {
        background: 'transparent',
        padding: 0,
        mt: 0
    }
};

interface ToolbarProps {
    cancel: () => void;
}

/**
 * The toolbar with Save and Cancel buttons.
 */
const Toolbar: React.FC<ToolbarProps> = ({ cancel }) => {
    return (
        <T sx={styles} className="add-child-toolbar">
            <ShsSaveButton label="Зберегти" sx={{ mr: 2 }} />
            <ShsButton label="Скасувати" styleType="outline" onClick={cancel} />
        </T>
    );
};

interface NewEducationProps {
    cancel: () => void;
}

/**
 * NewEducation form component for adding a new education record.
 */
export const NewEducation: React.FC<NewEducationProps> = ({ cancel }) => {
    const formPath = 'NewEducation';
    const record = useRecordContext();
    const educationCenterId = record.visit.educationCenterId;
    const [update] = useUpdate();
    const refresh = useRefresh();
    const notify = useNotify();
    const createPath = useCreatePath();
    const redirect = useRedirect();

    /**
     * Handles form submission by preparing the payload and triggering the update.
     * @param {any} data - Form data submitted by the user.
     */
    const handleSubmit = (data: any) => {
        const updates = { ...data[formPath] };
        delete data[formPath];
        data[ChildFormFields.VISIT] = {
            ...data[ChildFormFields.VISIT],
            ...updates,
            [ChildFormFields.EDUCATIONAL_SERVICE_END]: null
        };

        update(
            Resource.CHILDREN,
            {
                id: data.id,
                data: {
                    ...data
                },
                meta: { useMock: false, method: `update` }
            },
            {
                mutationMode: 'pessimistic',
                onSuccess: () => {
                    redirect(createPath({ resource: `children/${record.id}/show`, type: 'show' }));
                    refresh();
                    cancel();
                    notify('Дані збережено', { type: 'success', undoable: true });
                },
                onError: (error: any) => {
                    cancel();
                    notify(`Error: ${error?.message}`, { type: 'warning', undoable: true });
                }
            }
        );
    };
    return (
        <Grid item xs={12}>
            <SimpleForm sx={styles} toolbar={<Toolbar cancel={cancel} />} onSubmit={handleSubmit}>
                <Stack>
                    <Box sx={{ ...sectionTitleStyle, mb: 2 }}>Розпочати навчання знову</Box>
                    <ShsAutoCompleteWithLoad
                        source={`${formPath}.${ChildFormFields.HOSPITAL_DEPARTMENT}`}
                        pageUrl="hospital-departments"
                        keyField="id"
                        textField="departmentName"
                        resultLimit={100}
                        label="Відділення в лікарні"
                        filters={{ educationCenterId }}
                        required
                    />

                    <ShsDatePicker
                        source={`${formPath}.${ChildFormFields.EDUCATIONAL_AGREEMENT_DATE}`}
                        label="Дата згоди на надання освітніх послуг"
                        required
                    />
                </Stack>
            </SimpleForm>
        </Grid>
    );
};

export default NewEducation;
